







































































































































import {
  Vue,
  Component,
  Prop,
  Inject,
  Watch,
} from "@/shared/vuepack";
import { Validator } from "vee-validate";
import { FamilyMemberRelationship } from "@/shared/types";
import moment from "moment";
import Button from "@/shared/components/ui/Button.vue";
import Checkbox from "@/shared/components/ui/Checkbox.vue";
import Datepicker from "vuejs-datepicker";
import Input from "@/shared/components/ui/Input.vue";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { BankCustodian } from "@/admin/types";

interface SelectOption {
  value: FamilyMemberRelationship;
  text: string;
}

@Component({
  components: {
    Button,
    Checkbox,
    Datepicker,
    Input
  }
})
export default class FamilyMemberComponent extends Vue {
  custodianSettingsStore = useCustodianSettingsStore();

  minimumDateOfBirth = moment().subtract(18, "y");
  isSsnMasked = true;
  showSsnMaskToggle = true;
  dateOfBirth = "";

  @Prop()
  isOwner: boolean;

  @Prop()
  familyMember;

  @Prop()
  index: number;

  @Prop({ required: true })
  identifier: number;

  @Inject() $validator: Validator;

  mounted (): void {
    if (this.familyMember && this.familyMember.dateOfBirth) {
      this.dateOfBirth = moment(this.familyMember.dateOfBirth).format("YYYY-MM-DD");
    }

    if (!this.familyMember.ssn) {
      this.isSsnMasked = false;
      this.showSsnMaskToggle = false;
    }
  }

  onDateOfBirthSelected (dateOfBirth: any) {
    if (!this.familyMember) {
      return;
    }

    this.familyMember.dateOfBirth = dateOfBirth;
  }

  @Watch("allowDebitCard")
  allowDebitCardChanged (isNowAllowed) {
    if (!isNowAllowed && this.familyMember.debitCardHolder) {
      this.familyMember.debitCardHolder = false;
    }
  }

  get isHsaBank (): boolean {
    return this.custodianSettingsStore.userHsaCustodian === BankCustodian.HsaBank;
  }

  get familyMemberRelationshipOptions (): SelectOption[] {
    if (this.isHsaBank) {
      return [
        { value: FamilyMemberRelationship.Spouse, text: "Spouse" },
        { value: FamilyMemberRelationship.Dependent, text: "Dependent" }
      ];
    }

    return [
      { value: FamilyMemberRelationship.Spouse, text: "Spouse" },
      { value: FamilyMemberRelationship.Child, text: "Child" },
      { value: FamilyMemberRelationship.Other, text: "Other" }
    ];
  }

  get displayAccountOwnerDebitCardCopy (): boolean {
    return this.isOwner && !this.isHsaBank;
  }

  get displayFamilyMemberDebitCardCheckbox (): boolean {
    return !this.isOwner && !this.isHsaBank;
  }

  get allowSsnInput (): boolean {
    return this.custodianSettingsStore.allowFamilyMemberSsnInput;
  }

  removeFamilyMember (): void {
    this.$emit("deleted", this.index);
  }

  get familyMemberInitials (): string {
    const firstInitial = this.familyMember.firstName.charAt(0);
    const secondInitial = this.familyMember.lastName.charAt(0);

    return `${firstInitial}${secondInitial}`;
  }

  get allowDebitCard (): boolean {
    return (this.familyMember.dateOfBirth
        && !this.hasError("Date of birth")
        && this.isOldEnoughForHSACard);
  }

  get isOldEnoughForHSACard (): boolean {
    const dateOfBirthValue = this.familyMember.dateOfBirth;
    if (!dateOfBirthValue) {
      return false;
    }
    const parsedDateOfBirth = moment(dateOfBirthValue);
    return parsedDateOfBirth.isValid() && parsedDateOfBirth.isBefore(this.minimumDateOfBirth);
  }

  get scopeId (): string {
    return `${this.identifier}`;
  }

  hasError (name): boolean {
    const scopedId = `${this.identifier}.${name}`;
    return this.$validator.errors.has(scopedId);
  }

  getError (name): string {
    const scopedId = `${this.identifier}.${name}`;
    return this.$validator.errors.first(scopedId);
  }

  get lastFourSsn (): string {
    const ssnLength = this.familyMember.ssn.length;
    if (ssnLength) {
      return `XXX-XX-${this.familyMember.ssn.substring(7, ssnLength)}`;
    }

    return null;
  }

  get allowSsnMaskToggle (): boolean {
    const hasSsnValidationError = this.getError("Social Security Number");
    return this.allowSsnInput && (!this.familyMember.ssn || !hasSsnValidationError);
  }

  toggleSsnMask (): void {
    this.isSsnMasked = !this.isSsnMasked;
  }

  toggleDebitCardCheckbox (): void {
    if (!this.familyMember.ssn) {
      this.isSsnMasked = false;
      this.showSsnMaskToggle = false;
    }

    this.familyMember.debitCardHolder = !this.familyMember.debitCardHolder;
  }
}
